import React, { useState, useEffect }  from 'react'
import { View, StyleSheet, ScrollView, KeyboardAvoidingView, TouchableOpacity, Platform, Linking } from 'react-native'
import { Button, Text, Spinner, Modal, Icon, Toggle, useTheme } from '@ui-kitten/components'
import StyledTextInput from '../../components/StyledTextInput'
import * as Sentry from "@sentry/react-native"
import PlatformDependantSelect from '../../components/PlatformDependantSelect'
import { auth, deleteUsersFromAuth, emailAuthProvider, logout, updateOrganization, rootDb } from '../../global/Firebase/firebase'
import { organizationsApi } from '../../global/API/typedApiClient'
import { useTranslation } from 'react-i18next'
import { useAtomValue } from 'jotai'
import { organizationAtom } from '../../global/AtomsProvider'
import { CURRENCIES } from '../../utils/currencies'
import { MODULES } from '../../utils/organizationTypes'
import { sanitizeForFirestore } from '../../global/Firebase/productSanitize'

export default function OrgDetailScreen( { navigation, route } ) {
  const theme = useTheme()
  const { t } = useTranslation()
  const organization = useAtomValue(organizationAtom)

  // Create localized currencies with 'text' property for dropdown display
  const localizedCurrencies = CURRENCIES.map(item => ({...item, text: item.shortText + ' - ' + t('currencies.' + item.id)}))

  // Find matching localized currency by id (organization.currency doesn't have 'text' property)
  const getLocalizedCurrency = (orgCurrency) => {
    if (!orgCurrency?.id) return null
    return localizedCurrencies.find(c => c.id === orgCurrency.id) || orgCurrency
  }

  const [name, setName] = useState(organization?.name)
  const [currency, setCurrency] = useState(() => getLocalizedCurrency(organization?.currency))
  const [currencyDecimalPlaces, setCurrencyDecimalPlaces] = useState(organization?.currency?.currencyDecimalPlaces?.toString() || '0' )
  const [moduleEnabled, setModuleEnabled] = useState(organization?.moduleEnabled || {})
  const [deleteOrgModalVisible, setDeleteOrgModalVisible] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [saveError, setSaveError] = useState('')
  const [successMessageVisible, setSuccessMessageVisible] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  // Emit organization detail data for E2E testing
  useEffect(() => {
    if (organization?.id) {
      console.log('[ORGANIZATION_DETAIL_DATA]', JSON.stringify(sanitizeForFirestore({
        id: organization.id,
        name: organization.name,
        currency: organization.currency,
        moduleEnabled: organization.moduleEnabled,
      })));
    }
  }, [organization]);

  async function save() {
    
    if (name === '') {
      setSaveError(t('notAllFieldsFilled'))
      return
    }
    let currencyDecimalPlacesInt = parseInt(currencyDecimalPlaces)
    if (isNaN(currencyDecimalPlacesInt)) {
      setSaveError(t('organizationDetails.nanErrorText'))
      return
    }
    if (currencyDecimalPlacesInt < 0 || currencyDecimalPlacesInt > 5) {
      setSaveError(t('organizationDetails.decimalPlacesErrorText'))
      return
    }

    let updatedCurrency = {...currency, currencyDecimalPlaces: currencyDecimalPlacesInt}
    setSaveError('')
    setIsLoading(true)
    const info = {
      name, currency: updatedCurrency, moduleEnabled, 
    }
    try {
      await updateOrganization(organization.ref, info)
      setSuccessMessageVisible(true)
      setIsLoading(false)
    } catch (error) {
      Sentry.captureException(error)
      console.log(error)
      setSaveError(t('error'))
      setIsLoading(false)
    }
  }

  async function deleteOrg() {
    setIsLoading(true)
    auth.currentUser.reauthenticateWithCredential(emailAuthProvider.credential(email, password)).then(async () => {
      // Dual-write: delete from Firestore first, then soft-delete in Supabase
      await rootDb.collection('organizations').doc(organization.id).delete()
      await organizationsApi.deleteOrganization(organization.id)
      deleteUsersFromAuth()
      logout()
    }).catch((error) => {
      Sentry.captureException(error)
      setIsLoading(false)
      console.log(error)
      setSaveError(t('error'))
    })
  }



  return (
    <KeyboardAvoidingView style={{flex: 1}} behavior="height" keyboardVerticalOffset = {100}>
      <ScrollView style={styles.container} keyboardShouldPersistTaps='handled' keyboardDismissMode='on-drag'>
        <Text appearance='hint' style={{textAlign: 'center'}}>
          {organization?.id}
        </Text>
        <Text category='label' style={styles.label}>
          {t('organizationDetails.name')}
        </Text>
        <StyledTextInput
          value={name}
          onChangeText={text => setName(text)}
          returnKeyType='done'
          autoCapitalize='sentences'
        />
        <Text category='label' style={styles.label}>
          {t('organizationDetails.currency')}
        </Text>
        <PlatformDependantSelect
          data={localizedCurrencies}
          selectedOption={currency}
          onSelect={setCurrency}
          withSearch={true}
        />
        <Text category='label' style={styles.label}>
          {t('organizationDetails.currencyDecimalPlaces')}
        </Text>
        <StyledTextInput
          value={currencyDecimalPlaces}
          onChangeText={text => setCurrencyDecimalPlaces(text)}
          returnKeyType='done'
          autoCapitalize='sentences'
        />
        <Text category='label' style={styles.label}>
          {t('organizationDetails.enabledModules')}
        </Text>
        { MODULES.map((module, index) => (
          <View key={module.id} style={{marginBottom: 5, backgroundColor: theme['background-basic-color-1'], padding: 10, borderRadius: 10}}>
            <Toggle
              checked={moduleEnabled[module.id]}
              onChange={(value) => {
                var newR = {...moduleEnabled}
                newR[module.id] = value
                setModuleEnabled(newR)
              }}
              style={{alignSelf: 'flex-start', marginBottom: 5}}
            >
              {t('organizationDetails.' + module.id + 'Text')}
            </Toggle>
            <Text>
              {t('organizationDetails.' + module.id + 'Description')}
              <Text style={{color: 'blue'}}
                    onPress={() => Linking.openURL(t('organizationDetails.' + module.id + 'Link'))}>
                {t('organizationDetails.moreInfo')}
              </Text>
            </Text>
          </View>
        ))}
        
        {saveError != '' && (
          <Text category='p1' status='danger' style={styles.label}>
            {saveError}
          </Text>
        )}
        { successMessageVisible && (
          <>
          <Text status='success'>{t('success')}</Text>
          <Button onPress={() => navigation.goBack()} style={styles.submitButton}>
            {t('back')}
          </Button>
          </>
        )}
        {isLoading ? (
          <View style={styles.spinner}><Spinner /></View>
        ) : (
          !successMessageVisible && (
          <Button onPress={save} style={styles.submitButton}>
            {t('save')}
          </Button>
          )
        )}
        <Button status='danger' onPress={() => {navigation.navigate('ResetData')}} style={styles.submitButton}>
          {t('organizationDetails.resetData')}
        </Button>
        <Button status='danger' onPress={() => {setDeleteOrgModalVisible(true)}} style={styles.submitButton}>
          {t('organizationDetails.deleteOrg')}
        </Button>
        <Modal style={[styles.modal, {padding: 30, backgroundColor: theme['background-basic-color-1']}]} 
          visible={deleteOrgModalVisible} 
          backdropStyle={styles.backdrop} 
          onBackdropPress={() => setDeleteOrgModalVisible(false)}
        >
          <TouchableOpacity 
            style={{position: 'absolute', top: 10, right: 10, alignSelf: 'flex-end', alignItems: 'center', justifyContent: 'center'}} 
            onPress={() => {
              setDeleteOrgModalVisible(false)
            }}
          > 
            <Icon name='close-circle' width={32} height={32} fill={theme['text-basic-color']} />
          </TouchableOpacity>
          <Text>{t('organizationDetails.deleteOrgConfirmationText')}</Text>
          <Text category='label' style={styles.label}>
            {t('organizationDetails.email')}
          </Text>
          <StyledTextInput 
            value={email}
            onChangeText={setEmail}
            keyboardType="email-address"
            textContentType="emailAddress"
            autoFocus={true}
            autoCapitalize="none"
            autoCorrect={false}
            style={styles.input}
          />
          <Text category='label' style={styles.label}>
            {t('organizationDetails.password')}
          </Text>
          <StyledTextInput 
            value={password}
            onChangeText={setPassword}
            secureTextEntry={true}
            textContentType="password"
            autoCapitalize="none"
            autoCorrect={false}
            style={styles.input}
          />
          {isLoading ? (
            <View style={styles.spinner}><Spinner /></View>
          ) : (
            <>
              {saveError != '' && (
                <Text category='p1' status='danger' style={styles.label}>
                  {saveError}
                </Text>
              )}
              <Button status='danger' onPress={deleteOrg} style={styles.submitButton}>
                {t('organizationDetails.delete')}
              </Button>
            </>
          )}
        </Modal>
        
        <View style={{height: 100}}></View>
      </ScrollView>
    </KeyboardAvoidingView>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
  },
  label: {
    marginBottom: 8,
    marginTop: 16,
  },
  submitButton: {
    marginTop: 16,
  },
  spinner: {
    marginTop: 15,
    alignItems: 'center'
  },
  modal: {
    top: 100,
    width: '80%',
    height: '80%',
    borderRadius: 20,
  },
  backdrop: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
});